import { supabase } from '@/integrations/supabase/client';
import { getApiPath } from './api';

/**
 * Detecta se a URL aponta para o nosso backend.
 * Em produção, qualquer URL relativa (/) ou que comece com /api deve ser considerada backend.
 * Também verifica se a URL aponta para VITE_API_URL.
 */
function isBackendUrl(url: string): boolean {
  try {
    // URLs relativas são sempre tratadas como chamadas ao backend
    if (url.startsWith('/')) return true;

    // Verifica se a URL aponta para o backend configurado em VITE_API_URL
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl && apiUrl !== '/api') {
      try {
        const apiUrlObj = new URL(apiUrl);
        const urlObj = new URL(url);
        // Se o origin da URL for o mesmo do VITE_API_URL, é uma chamada ao backend
        if (urlObj.origin === apiUrlObj.origin) {
          return true;
        }
      } catch (e) {
        // Se não conseguir fazer parse, continua com outras verificações
      }
    }

    const u = new URL(url, window.location.origin);
    
    // Em produção, verificamos se o origin é o mesmo do frontend
    // Em desenvolvimento, permitimos localhost:3001
    return (
      u.origin === window.location.origin ||
      u.origin.includes('localhost:3001')
    );
  } catch (error) {
    // Se não for uma URL válida, pode ser um caminho relativo
    return url.startsWith('/');
  }
}

export type FetchWithAuthInit = RequestInit & {
  /** Sobrescreve o timeout padrão (30s; rotas `/gerar` usam 120s). */
  timeoutMs?: number;
};

const FETCH_TIMEOUT_PADRAO_MS = 30_000;
const FETCH_TIMEOUT_GERAR_PDF_MS = 120_000;

// Armazena o fetch original para evitar recursão infinita
const originalFetch = window.fetch.bind(window);

export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: FetchWithAuthInit = {}
): Promise<Response> {
  const { timeoutMs: timeoutMsOpt, ...restInit } = init;
  // Clona o init para não alterar o objeto original
  const requestInit: RequestInit = { ...restInit, headers: { ...(restInit.headers || {}) } };

  // Se o body for FormData, não definir Content-Type (deixa o navegador definir com boundary)
  if (restInit.body instanceof FormData) {
    const headers = requestInit.headers as Record<string, string>;
    if (headers['Content-Type']) {
      delete headers['Content-Type'];
    }
  }

  let url = typeof input === 'string' ? input : (input as Request).url;
  const originalUrl = url;

  // Se for uma URL relativa que começa com /api, converter para absoluta se necessário
  if (url.startsWith('/api')) {
    url = getApiPath(url);
    // Log de debug em desenvolvimento
    if (import.meta.env.DEV) {
      console.log('🔍 fetchWithAuth - URL convertida:', { original: originalUrl, converted: url });
    }
  }

  // Correção de segurança: Se a URL tiver localhost:3001 e não estivermos em ambiente local
  if (url.includes('localhost:3001') && !window.location.hostname.includes('localhost')) {
    console.warn('⚠️ Redirecionando chamada de localhost para API:', url);
    // Se VITE_API_URL estiver definida, usar ela, senão manter relativo
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl && apiUrl !== '/api') {
      url = url.replace(/https?:\/\/localhost:3001/, apiUrl);
    } else {
      url = url.replace(/https?:\/\/localhost:3001/, '');
      if (!url.startsWith('/api')) {
        url = '/api' + (url.startsWith('/') ? '' : '/') + url;
      }
    }
  }

  if (isBackendUrl(url)) {
    // Busca o token da sessão atual do Supabase
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    
    if (token) {
      (requestInit.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        console.log('🔐 fetchWithAuth - Token adicionado ao header');
      }
    } else {
      if (import.meta.env.DEV) {
        console.warn('⚠️ fetchWithAuth - Nenhum token encontrado na sessão');
      }
    }
  }

  if (import.meta.env.DEV) {
    console.log('🌐 fetchWithAuth - Fazendo requisição:', { 
      url, 
      method: requestInit.method || 'GET',
      hasAuth: !!(requestInit.headers as Record<string, string>)?.['Authorization']
    });
  }

  const isGeracaoPdf = /\/gerar(?:\?|$)/.test(url);
  const timeoutMs =
    timeoutMsOpt ?? (isGeracaoPdf ? FETCH_TIMEOUT_GERAR_PDF_MS : FETCH_TIMEOUT_PADRAO_MS);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await originalFetch(url, {
      ...requestInit,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(
        `Tempo de espera esgotado. A requisição demorou mais de ${Math.round(timeoutMs / 1000)} segundos.`
      );
    }
    throw error;
  }
}
